<template>
    <div>
        <v-container class="pa-5" fluid>
            <ConfirmDialog
                :is-open="deleteContractorDialog"
                :data="outdoorsLocalizationContractor"
                @on-confirm="removeOutdoorsLocalizationContractor(outdoorsLocalizationContractor)"
                @on-abort="closeDeleteContractorDialog"
            >
                <template #title>
                    Usuwanie zleceniobiorcy
                </template>
                <template #content>
                    Czy na pewno chcesz usunąć zleceniobiorcę?
                </template>
                <template #confirmButton>
                    Usuń
                </template>
            </ConfirmDialog>

            <v-row align="center" dense>
                <v-col cols="auto">
                    <page-title>Zleceniobiorcy</page-title>
                </v-col>
                <v-col cols="auto">
                    <SelectMenu />
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="12" lg="4">
                    <v-btn
                        color="secondary"
                        medium
                        data-sentry-label="Dodaj zleceniobiorcę"
                        @click="createOutdoorsContractor()"
                    >
                        <v-icon left>
                            mdi-plus
                        </v-icon>
                        Dodaj zleceniobiorcę
                    </v-btn>
                </v-col>
                <v-col cols="12" sm="8">
                    <v-container fluid class="pa-0">
                        <v-row justify="end" dense>
                            <v-col cols="12" md="8" lg="4">
                                <v-text-field
                                    v-model="filters.name"
                                    append-icon="mdi-magnify"
                                    label="Szukaj..."
                                    single-line
                                    clearable
                                    hide-details
                                    solo
                                    @keyup.enter="changeFilter"
                                    @click:append="changeFilter"
                                    @click:clear="clearFilter('name')"
                                />
                            </v-col>
                        </v-row>
                    </v-container>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12">
                    <v-card>
                        <v-data-table
                            :headers="headers"
                            :items="outdoorsLocalizationContractors"
                            :loading="outdoorsLocalizationContractorsLoading"
                            :footer-props="footerProps"
                            :server-items-length="pagination.itemsLength"
                            :mobile-breakpoint="0"
                            name="outdoorsLocalizationContractorsData"
                            @pagination="updatePagination"
                        >
                            <template #item="{item}">
                                <tr :class="{'isSelected': selectedContractorId == item.id}">
                                    <td>
                                        {{ item.name }}
                                    </td>
                                    <td>
                                        <template v-if="item.contactMail">
                                            {{ parseEmptyValue(item.contactMail) }}
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="item.street">
                                            {{ parseEmptyValue(item.street) }}
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="item.postCode">
                                            {{ parseEmptyValue(item.postCode) }}
                                        </template>
                                    </td>
                                    <td>
                                        <template v-if="item.city">
                                            {{ parseEmptyValue(item.city) }}
                                        </template>
                                    </td>
                                    <td class="text-center">
                                        <div class="d-inline-flex">
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Otwórz"
                                                        v-on="on"
                                                        @click="openOutdoorsContractor(item.id)"
                                                    >
                                                        <v-icon color="blue">
                                                            mdi-ballot
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Otwórz</span>
                                            </v-tooltip>
                                            <v-tooltip top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń"
                                                        v-on="on"
                                                        @click="showDeleteContractor(item.id)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń</span>
                                            </v-tooltip>
                                        </div>
                                    </td>
                                </tr>
                            </template>
                        </v-data-table>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'
import pagination from '@/mixins/dataTablePagination'
import tooltipDelay from '@/mixins/tooltip'
import SelectMenu from '@/views/Outdoors/Common/SelectMenu'
import filters from '@/mixins/dataTableFilters'
import dataTableClassSetter from '@/mixins/dataTableClassSetter'
import { errorHandler } from '@/utils/errorHandler'
import parse from '@/mixins/parse'

export default {
    name: 'OutdoorsContractorsList',
    components: {
        PageTitle,
        ConfirmDialog,
        SelectMenu
    },
    mixins: [
        pagination,
        tooltipDelay,
        filters,
        parse,
        dataTableClassSetter
    ],
    beforeRouteLeave(to, from, next) {
        if (!this.filtersCleared) {
            this.$store.dispatch('outdoors/outdoorsSettings/clearContractorFilters')
            this.clearSelectedListItemId()
        }
        this.resetFiltersCleared()
        next()
    },
    data() {
        return {
            outdoorsLocalizationContractor: {
                name: null,
                contactPerson: null,
                city: null,
                street: null,
                postCode: null,
                contactMail: null,
                phoneNumber: null
            },
            deleteContractorDialog: false,
            localizationLoading: false,
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false
                },
                {
                    text: 'Email',
                    value: 'contactMail',
                    sortable: false
                },
                {
                    text: 'Adres',
                    value: 'street',
                    sortable: false,
                },
                {
                    text: 'Kod pocztowy',
                    value: 'postCode',
                    sortable: false
                },
                {
                    text: 'Miasto',
                    value: 'city',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        outdoorsLocalizationContractors() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationContractors.outdoorsLocalizationContractors
        },
        outdoorsLocalizationContractorsLoading() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationContractors.outdoorsLocalizationContractorsLoading
        },
        savedFilters() {
            return this.$store.state.outdoors.outdoorsSettings.outdoorsLocalizationContractors.filters
        },
        selectedContractorId() {
            return this.$store.state.common.selectedListItemId
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    beforeMount() {
        if (!this.savedPagination.pagination) {
            this.resetPagination()
        }
    },
    mounted() {
        this.getContractors()
    },
    methods: {
        getContractors() {
            this.$store.dispatch('outdoors/outdoorsSettings/getContractors', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createOutdoorsContractor() {
            this.localizationLoading = true
            this.$router.push({ name: 'outdoorsSettingsOutdoorsContractorsOutdoorsContractorCreate' })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.localizationLoading = false
                })
        },
        openOutdoorsContractor(outdoorsContractorId) {
            this.localizationLoading = true
            this.savePagination(this.$options.name)
            this.saveFiltersCleared()
            this.setSelectedListItemId(outdoorsContractorId)
            this.$router.push({ name: 'outdoorsSettingsOutdoorsContractorsOutdoorsContractorEdit', params: {id: outdoorsContractorId } })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.localizationLoading = false
                })
        },
        async getContractor(outdoorsLocalizationContractorId) {
            return await this.$store.dispatch('outdoors/outdoorsSettings/getContractor', outdoorsLocalizationContractorId)
                .then(response => {
                    this.outdoorsLocalizationContractor = response.data
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showDeleteContractor(outdoorsLocalizationContractorId) {
            this.getContractor(outdoorsLocalizationContractorId)
                .then(() => {
                    this.deleteContractorDialog = true
                })
        },
        closeDeleteContractorDialog() {
            this.deleteContractorDialog = false
            setTimeout(() => {
                this.outdoorsLocalizationContractor = {}
            }, 500)
        },
        removeOutdoorsLocalizationContractor() {
            this.actionLoading = true
            this.$store.dispatch('outdoors/outdoorsSettings/deleteContractor', this.outdoorsLocalizationContractor.id )
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.getContractors()
                    this.closeDeleteContractorDialog()
                })
        },
        changeFilter() {
            this.$store.dispatch('outdoors/outdoorsSettings/setContractorFilters', this.filters)
            this.resetPagination()
            this.getContractors()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending && !this.savedPagination.pagination) {
                this.getContractors()
            }
            this.clearPaginationPending()
        }
    }
}
</script>

